/**
 * Who is sending: the footer rule after Terje's decision 6/10-2026. A mail
 * must clearly identify its sender (markedsføringsloven § 10, DDG § 5, EU
 * e-commerce directive art. 5–6) and the legal notice / Impressum must be
 * easy to reach — a link is enough. A postal address is never required:
 * NoPain's 20 archived Mailchimp newsletters never had one, only
 * "Copyright (C) 2026 NoPain® Cream" / "You were subscribed to the
 * newsletter from NoPain® Cream".
 *
 * Shared by the save-time server check (`./server-check.ts`), the HTML
 * import report, the pre-send check (`../send/preflight.ts`), the text
 * part and the section editor's analysis (`../sections/analyse.ts`). The
 * last one also runs in the admin browser, so this module must stay free
 * of `sanitize-html` and other Node-only imports.
 */

/** Brand kit values the check may compare against. Both optional: without them the check falls back to text patterns only. */
export interface SenderContext {
  /** Brand kit "Sender details" (`brand_kit.company_address`), e.g. "NoPain® Cream". */
  senderDetails?: string | null | undefined;
  /** Brand kit legal notice link (`brand_kit.legal_notice_url`). */
  legalNoticeUrl?: string | null | undefined;
}

/** The two brand kit columns as a {@link SenderContext} (a `BrandKitRow` fits). */
export function senderContextOf(brandKit: {
  company_address: string | null;
  legal_notice_url: string | null;
}): SenderContext {
  return { senderDetails: brandKit.company_address, legalNoticeUrl: brandKit.legal_notice_url };
}

/** Shortest sender-details text that counts as naming the sender ("NoPain® Cream" easily; a stray "x" does not). */
export const MIN_SENDER_DETAILS_LENGTH = 3;

/** `{{ company_address }}`, with or without a `| default: "..."` filter. */
export const COMPANY_ADDRESS_MERGE_FIELD_PATTERN = /\{\{\s*company_address\b[^}]*\}\}/;
/** `{{ legal_notice_url }}`, with or without a `| default: "..."` filter. */
export const LEGAL_NOTICE_MERGE_FIELD_PATTERN = /\{\{\s*legal_notice_url\b[^}]*\}\}/;

/**
 * A written postal address: a 4- or 5-digit postal code (Danish, German)
 * followed by text, or a company-form suffix (matched case-sensitively so
 * short forms don't hit ordinary words). No longer required anywhere — it
 * only keeps footers that DO contain an address passing as "names the
 * sender", so templates saved before 6/10-2026 keep working.
 */
export const ADDRESS_HEURISTIC_PATTERN =
  /\b\d{4,5}\b\s+[^\d,]{2,}|\b(?:ApS|A\/S|I\/S|IVS|FZCO|FZE|FZ-LLC|LLC|GmbH|Ltd|Inc|B\.V\.|SARL|SAS|S\.L\.|S\.r\.l\.)(?!\w)/;

/**
 * Lines that name the sender in the usual footer wording, on visible text.
 * Lenient on purpose: "Copyright (C) 2026 NoPain® Cream", "© NoPain® Cream",
 * "You were subscribed to the newsletter from NoPain® Cream", "Our mailing
 * address is: NoPain® Cream", "Sent by NoPain® Cream".
 */
const SENDER_LINE_PATTERNS: readonly RegExp[] = [
  /(?:©|\(c\)|\bcopyright\b)\s*(?:©|\(c\))?\s*(?:\d{4}(?:\s*[-–]\s*\d{4})?)?\s*,?\s*[\p{L}\p{N}]/iu,
  /\b(?:newsletters?|e-?mails?|mails?|updates)\s+from\s+[\p{L}\p{N}]/iu,
  /\bmailing address is:?\s*[\p{L}\p{N}]/iu,
  /\bsent\s+by\s+[\p{L}\p{N}]/iu,
];

/**
 * Class on the sender line a footer *block* renders (core `render.ts`,
 * the admin's `blocks.ts`). The block editor already requires its
 * "Sender details" field, and that text can be any name ("NoPain® Cream"),
 * so the HTML check takes a marked line of at least
 * {@link MIN_SENDER_DETAILS_LENGTH} characters as naming the sender.
 */
export const SENDER_DETAILS_CLASS = 'sender-details';

const SENDER_DETAILS_ELEMENT_PATTERN =
  /<([a-z][a-z0-9]*)\b[^>]*\bclass\s*=\s*(?:"[^"]*\bsender-details\b[^"]*"|'[^']*\bsender-details\b[^']*')[^>]*>([\s\S]*?)<\/\1\s*>/gi;

/** True when a block footer's marked sender line holds usable sender details. */
function hasMarkedSenderLine(html: string): boolean {
  for (const match of html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .matchAll(SENDER_DETAILS_ELEMENT_PATTERN)) {
    if (isUsableSenderDetails(visibleText(match[2] ?? ''))) return true;
  }
  return false;
}

/** Link text or href of a legal notice / Impressum page. */
const LEGAL_NOTICE_TEXT_PATTERN = /\b(?:legal[ -]notice|impressum|imprint)\b/i;
const LEGAL_NOTICE_HREF_PATTERN = /legal[-_]?notice|impressum|imprint/i;

const ANCHOR_PATTERN = /<a\b([^>]*)>([\s\S]*?)<\/a\s*>/gi;
const HREF_ATTRIBUTE_PATTERN = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  copy: '©',
  reg: '®',
  trade: '™',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, body: string) => {
    if (body[0] === '#') {
      const code =
        body[1] === 'x' || body[1] === 'X'
          ? Number.parseInt(body.slice(2), 16)
          : Number.parseInt(body.slice(1), 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : whole;
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? whole;
  });
}

/** What a reader sees: no comments, `<head>`/`<style>`/`<script>` content or tags, entities decoded, whitespace collapsed. */
export function visibleText(html: string): string {
  const withoutHidden = html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<(head|style|script|title)\b[\s\S]*?<\/\1\s*>/gi, ' ');
  return decodeEntities(withoutHidden.replace(/<[^>]*>/g, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeForCompare(text: string): string {
  return text.replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Letters and digits only, lower-cased: "NoPain® Cream" → "nopaincream". */
function compactName(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

/** True when trimmed sender details are long enough to name the sender. */
export function isUsableSenderDetails(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim().length >= MIN_SENDER_DETAILS_LENGTH;
}

/** True when `text` has one of the usual "© … / newsletter from …" sender lines. */
export function hasSenderLine(text: string): boolean {
  return SENDER_LINE_PATTERNS.some((pattern) => pattern.test(text));
}

interface Anchor {
  href: string;
  text: string;
}

function anchorsOf(html: string): Anchor[] {
  const anchors: Anchor[] = [];
  for (const match of html.replace(/<!--[\s\S]*?-->/g, ' ').matchAll(ANCHOR_PATTERN)) {
    const attrs = match[1] ?? '';
    const href = HREF_ATTRIBUTE_PATTERN.exec(attrs);
    anchors.push({
      href: decodeEntities((href?.[1] ?? href?.[2] ?? href?.[3] ?? '').trim()),
      text: visibleText(match[2] ?? ''),
    });
  }
  return anchors;
}

function hostOf(url: string): string | null {
  const match = /^(?:https?:)?\/\/([^/?#:]+)/i.exec(url.trim());
  if (!match?.[1]) return null;
  return match[1].toLowerCase().replace(/^www\./, '');
}

function sameUrl(a: string, b: string): boolean {
  const clean = (u: string) => u.trim().replace(/\/+$/, '').toLowerCase();
  return clean(a) !== '' && clean(a) === clean(b);
}

/** True when the HTML links to a legal notice: `{{ legal_notice_url }}`, the brand kit URL, or a link labelled/pointing at "Legal notice", "Impressum" or "Imprint". */
export function hasLegalNoticeLink(html: string, ctx: SenderContext = {}): boolean {
  if (LEGAL_NOTICE_MERGE_FIELD_PATTERN.test(html)) return true;
  const brandUrl = ctx.legalNoticeUrl?.trim() ?? '';
  return anchorsOf(html).some(
    (a) =>
      a.href !== '' &&
      ((brandUrl !== '' && sameUrl(a.href, brandUrl)) ||
        LEGAL_NOTICE_TEXT_PATTERN.test(a.text) ||
        LEGAL_NOTICE_HREF_PATTERN.test(a.href)),
  );
}

/**
 * True when the HTML links to the sender's own website: a host that is the
 * brand kit legal notice URL's host, or whose name contains the compacted
 * sender details ("NoPain® Cream" → nopaincream.com). Only used for
 * imported HTML with a brand kit at hand — a footer-less Mailchimp mail
 * whose logo and "Shop now" link go to the brand's site (NoPain's Black
 * Week mails) still makes the sender identifiable within one click
 * (BGH I ZR 228/03 accepts two).
 */
function linksToOwnSite(html: string, ctx: SenderContext): boolean {
  const legalHost = ctx.legalNoticeUrl ? hostOf(ctx.legalNoticeUrl) : null;
  const name = isUsableSenderDetails(ctx.senderDetails) ? compactName(ctx.senderDetails) : '';
  if (legalHost === null && name.length < MIN_SENDER_DETAILS_LENGTH) return false;
  return anchorsOf(html).some((a) => {
    const host = hostOf(a.href);
    if (host === null) return false;
    if (legalHost !== null && (host === legalHost || host.endsWith(`.${legalHost}`))) return true;
    return name.length >= MIN_SENDER_DETAILS_LENGTH && compactName(host).includes(name);
  });
}

/**
 * How the mail names its sender, most specific first:
 * - `merge_field`: `{{ company_address }}`, filled from the brand kit at send;
 * - `written`: the brand kit sender text, a footer block's marked sender
 *   line, a "© … / newsletter from …" line, or (backwards compatible) a
 *   written postal address;
 * - `legal_notice`: no sender line, but a legal notice link or (with a
 *   brand kit) a link to the sender's own site, which names the sender one
 *   click away;
 * - `missing`: none of these.
 */
export type SenderKind = 'merge_field' | 'written' | 'legal_notice' | 'missing';

export function footerSenderKind(html: string, ctx: SenderContext = {}): SenderKind {
  if (COMPANY_ADDRESS_MERGE_FIELD_PATTERN.test(html)) return 'merge_field';
  const text = visibleText(html);
  if (
    isUsableSenderDetails(ctx.senderDetails) &&
    normalizeForCompare(text).includes(normalizeForCompare(ctx.senderDetails))
  ) {
    return 'written';
  }
  // The address heuristic also runs on the raw HTML, exactly as the old
  // address check did, so nothing that passed before 6/10-2026 fails now.
  if (
    hasMarkedSenderLine(html) ||
    hasSenderLine(text) ||
    ADDRESS_HEURISTIC_PATTERN.test(text) ||
    ADDRESS_HEURISTIC_PATTERN.test(html)
  ) {
    return 'written';
  }
  if (hasLegalNoticeLink(html, ctx) || linksToOwnSite(html, ctx)) return 'legal_notice';
  return 'missing';
}

/** The user-facing message when {@link footerSenderKind} is `missing`. */
export const MISSING_SENDER_MESSAGE = 'The footer must say who is sending (e.g. your brand name).';
